'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';

/** The column a table is sorted by, and which way. */
export interface TableSort<Key extends string> {
  key: Key;
  direction: 'ascending' | 'descending';
}

/**
 * How a table is sorted: a click on a column sorts ascending, the next descending, the third
 * returns to the table's own order, so that order is never lost.
 */
export const useTableSort = () => {
  const $ = _c(3);
  const [sort, setSort] = useState();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = key => setSort(current => {
      if (current?.key !== key) {
        return {
          key,
          direction: "ascending"
        };
      }
      return current.direction === "ascending" ? {
        key,
        direction: "descending"
      } : undefined;
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const toggle = t0;
  let t1;
  if ($[1] !== sort) {
    t1 = {
      sort,
      toggle
    };
    $[1] = sort;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/**
 * The rows in the given sort, or as they came. Numbers by size, text as the reader's language
 * orders it; rows that tie keep their order.
 */
export const sortRows = <Row, Key extends string>(rows: readonly Row[], sort: TableSort<Key> | undefined, valueOf: (row: Row, key: Key) => number | string, locale: string): Row[] => {
  if (sort === undefined) return [...rows];
  const sign = sort.direction === 'ascending' ? 1 : -1;
  return rows.toSorted((a, b) => {
    const left = valueOf(a, sort.key);
    const right = valueOf(b, sort.key);
    const order = typeof left === 'number' && typeof right === 'number' ? left - right : String(left).localeCompare(String(right), locale);
    return sign * order;
  });
};